import {
  Alert,
  Anchor,
  Box,
  Button,
  Card,
  Center,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconPlaneTilt } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { useSession } from '../../app/session';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';

export function LoginPage() {
  const { t } = useTranslation();
  const { signIn, backendUnavailable } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const form = useForm({
    initialValues: { email: '', password: '' },
    validate: {
      email: (value) => (/^\S+@\S+\.\S+$/.test(value) ? null : t('common.requiredField')),
      password: (value) => (value.length ? null : t('common.requiredField')),
    },
  });

  const submit = form.onSubmit(async (values) => {
    setError(null);
    setIsPending(true);
    try {
      await signIn(values.email, values.password);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 400) {
        setError(t('auth.signInFailed'));
      } else if (caught instanceof ApiError && caught.isServerError) {
        setError(t('common.serverUnavailable'));
      } else if (
        caught instanceof ApiError &&
        caught.detail &&
        !caught.detail.startsWith('Request failed')
      ) {
        setError(caught.detail);
      } else {
        setError(t('common.error'));
      }
    } finally {
      setIsPending(false);
    }
  });

  return (
    <Box
      mih="100vh"
      style={{
        background:
          'linear-gradient(160deg, var(--mantine-color-aero-9) 0%, var(--mantine-color-aero-6) 55%, var(--mantine-color-aero-4) 100%)',
      }}
    >
      <Group justify="flex-end" p="md">
        <LanguageSwitcher />
      </Group>
      <Center px="md" pb={80}>
        <Stack w={400} maw="100%" gap="lg" mt={40}>
          <Stack gap={4} align="center" c="white">
            <IconPlaneTilt size={40} />
            <Title order={1} c="white">
              {t('app.name')}
            </Title>
            <Text fz="sm" opacity={0.85}>
              {t('auth.signInSubtitle')}
            </Text>
          </Stack>

          <Card p="lg" radius="lg" shadow="lg" withBorder={false}>
            <form onSubmit={submit}>
              <Stack gap="md">
                <Title order={2}>{t('auth.signInTitle')}</Title>

                {error || backendUnavailable ? (
                  <Alert color="red" icon={<IconAlertCircle size={16} />} variant="light">
                    {error ?? t('common.serverUnavailable')}
                  </Alert>
                ) : null}

                <TextInput
                  label={t('auth.email')}
                  type="email"
                  autoComplete="username"
                  autoFocus
                  required
                  {...form.getInputProps('email')}
                />
                <PasswordInput
                  label={t('auth.password')}
                  autoComplete="current-password"
                  required
                  {...form.getInputProps('password')}
                />
                <Button type="submit" fullWidth loading={isPending} size="md">
                  {t('action.signIn')}
                </Button>
              </Stack>
            </form>
          </Card>

          <Text fz="xs" c="white" opacity={0.8} ta="center">
            <Anchor href="/admin/" c="white" underline="always">
              {t('nav.admin')}
            </Anchor>
          </Text>
        </Stack>
      </Center>
    </Box>
  );
}
